import { Box } from '@mantine/core'
import { useForm, zodResolver } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import { useIsMutating } from '@tanstack/react-query'

import { changePasswordFormSchema } from '@/modules/profile/components/ChangePasswordForm/ChangePasswordForm.schema'
import type { ChangePasswordFormSchema } from '@/modules/profile/components/ChangePasswordForm/ChangePasswordForm.types'
import { changePasswordFormDefaultValues } from '@/modules/profile/components/ChangePasswordForm/ChangePasswordForm.values'

import { Button } from '@/components/Button/Button'
import { FlexInputWrapper } from '@/components/FlexInputWrapper/FlexInputWrapper'
import { Text } from '@/components/Text/Text'
import { TextInput } from '@/components/TextInput/TextInput'

import { useUserServiceUserControllerChangePassword } from '@/__generated__/queries'

export function ChangePasswordForm() {
  const isMutating = useIsMutating({
    exact: true,
  })

  const form = useForm<ChangePasswordFormSchema>({
    initialValues: changePasswordFormDefaultValues,
    validate: zodResolver(changePasswordFormSchema),
  })

  const changePasswordMutation = useUserServiceUserControllerChangePassword({
    onSuccess: () => {
      notifications.show({
        color: 'green',
        message: 'Password has been changed successfully',
        title: 'Password changed',
      })

      form.reset()
    },
  })

  const handleSubmit = form.onSubmit(values => {
    changePasswordMutation.mutate({
      requestBody: values,
    })
  })

  return (
    <Box component="form" mt={8} noValidate onSubmit={handleSubmit}>
      <Text mb={24} variant="body_2">
        Confirm your current password before setting a new one.
      </Text>

      <FlexInputWrapper
        isLast
        leftInput={
          <TextInput
            label="Current password"
            placeholder="Type your current password"
            type="password"
            {...form.getInputProps('currentPassword')}
          />
        }
      />

      <FlexInputWrapper
        isLast
        leftInput={
          <TextInput
            label="New password"
            placeholder="Type your new password"
            type="password"
            {...form.getInputProps('newPassword')}
          />
        }
      />

      <FlexInputWrapper
        isLast
        leftInput={
          <TextInput
            label="Confirm new password"
            placeholder="Confirm your new password"
            type="password"
            {...form.getInputProps('confirmPassword')}
          />
        }
      />

      <Button
        loading={!!isMutating}
        mt={{ base: 16, md: 100 }}
        type="submit"
        variant="primary"
      >
        Save
      </Button>
    </Box>
  )
}
